import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { getIndexerErrorInfo } from '../api/errors';
import {
  useGenerateIndexNowKeyMutation,
  useTestIndexNowKeyMutation,
} from '../api/indexerApi';
import { useProject } from '../lib/project-context';
import { copyText } from '../lib/utils';
import { IndexerIcon } from './IndexerIcon';

function defaultDomain(project: {
  indexNowDomain: string | null;
  siteUrl: string | null;
  name: string;
}): string {
  const raw =
    project.indexNowDomain?.trim() ||
    project.siteUrl?.trim() ||
    project.name.trim() ||
    '';
  if (!raw) return '';
  try {
    if (/^https?:\/\//i.test(raw)) return new URL(raw).origin;
    return `https://${raw.replace(/\/$/, '')}`;
  } catch {
    return raw;
  }
}

export function IndexNowSettingsPanel() {
  const { project, projectRef, refresh, applyProject } = useProject();
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState<'generate' | 'test' | null>(null);
  const [message, setMessage] = useState<{
    variant: 'success' | 'danger';
    text: string;
  } | null>(null);
  const [copied, setCopied] = useState<'key' | 'url' | null>(null);
  const [generateIndexNowKey] = useGenerateIndexNowKeyMutation();
  const [testIndexNowKey] = useTestIndexNowKeyMutation();

  useEffect(() => {
    if (!project) return;
    setDomain(defaultDomain(project));
  }, [project?.id, project?.indexNowDomain, project?.siteUrl, project?.name]);

  const configured = Boolean(project?.indexNowKey && project?.indexNowKeyFileUrl);

  const exampleOrigin = (() => {
    const raw = domain.trim();
    if (!raw) return 'https://example.com';
    try {
      if (/^https?:\/\//i.test(raw)) return new URL(raw).origin;
      return `https://${raw.replace(/\/$/, '')}`;
    } catch {
      return raw.replace(/\/$/, '') || 'https://example.com';
    }
  })();
  const exampleKey = project?.indexNowKey?.trim() || 'abc123xyz';
  const exampleFileUrl =
    project?.indexNowKeyFileUrl?.trim() ||
    `${exampleOrigin}/${exampleKey}.txt`;

  if (!project || !projectRef) return null;

  async function onCopy(kind: 'key' | 'url', value: string) {
    const ok = await copyText(value);
    if (!ok) return;
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  }

  async function onGenerate() {
    if (!project || !projectRef || busy) return;
    const trimmed = domain.trim();
    if (!trimmed) {
      setMessage({ variant: 'danger', text: 'دامنه IndexNow را وارد کنید' });
      return;
    }

    setBusy('generate');
    setMessage(null);
    try {
      const data = await generateIndexNowKey({
        projectId: projectRef,
        domain: trimmed,
      }).unwrap();

      applyProject({
        ...project,
        indexNowDomain: trimmed,
        indexNowKey: data.key,
        indexNowKeyFileUrl: data.keyFileUrl,
      });
      await refresh();
      setMessage({
        variant: 'success',
        text: 'کلید ساخته شد. فایل کلید را با توجه به راهنمای بالا روی دامنه خود منتشر کنید.',
      });
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'ساخت کلید ناموفق بود',
      });
    } finally {
      setBusy(null);
    }
  }

  async function onTest() {
    if (!project || !projectRef || busy || !configured) return;

    setBusy('test');
    setMessage(null);
    try {
      const data = await testIndexNowKey(projectRef).unwrap();
      const urlHint = data.testUrl ? ` برای ${data.testUrl}` : '';
      const detail = data.indexNow.messageFa
        .replace(/URLها/g, 'آدرس‌ها')
        .replace(/URL/g, 'آدرس');
      if (data.success) {
        setMessage({
          variant: 'success',
          text: `تست موفق بود${urlHint}. ${detail} (HTTP ${data.httpStatus})`,
        });
      } else {
        setMessage({
          variant: 'danger',
          text: `تست ناموفق${urlHint}. ${detail} (HTTP ${data.httpStatus})`,
        });
      }
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'تست کلید ناموفق بود',
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>سایر موتورهای جستجو (Bing و Yandex)</span>
        <span
          className={`badge ${configured ? 'text-bg-primary' : 'text-bg-warning'}`}
        >
          {configured ? 'تنظیم شده' : 'تنظیم نشده'}
        </span>
      </Card.Header>
      <Card.Body>
        <IconAlert variant="info" className="mb-3 align-items-start">
          <div className="text-justify pe-3">
            <p className="fw-semibold mb-2 d-inline-flex align-items-center gap-2">
              <IndexerIcon name="question-circle" />
              <span>راهنما</span>
            </p>
            <ol className="mb-0 ps-3">
              <li className="mb-2">
                یک فایل متنی با <strong>نام دقیق کلید</strong> بسازید و در ریشهٔ
                دامنه قرار دهید.
                <div className="mt-1">
                  مثال: سایت <code dir="ltr">{exampleOrigin}</code> و کلید{' '}
                  <code dir="ltr">{exampleKey}</code> → آدرس فایل باید{' '}
                  <code dir="ltr">{exampleFileUrl}</code> باشد.
                </div>
              </li>
              <li className="mb-0">
                محتوای فایل فقط همان کلید باشد (بدون فاصله یا متن اضافه). وقتی
                آدرس فایل را در مرورگر باز می‌کنید باید فقط مثلاً{' '}
                <code dir="ltr">{exampleKey}</code> را ببینید. بعد از انتشار فایل،
                با «تست کلید» یک درخواست ایندکس واقعی برای همین دامنه ارسال
                می‌شود تا از صحت کلید مطمئن شوید.
              </li>
            </ol>
          </div>
        </IconAlert>

        <Form.Group className="mb-3">
          <Form.Label>دامنه</Form.Label>
          <Form.Control
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="https://example.com"
            disabled={Boolean(busy)}
            dir="ltr"
            className="text-start"
          />
          <Form.Text className="text-secondary">
            معمولاً همان origin سایت است (مثلاً{' '}
            <span dir="ltr">https://mizfa.tools</span>).
          </Form.Text>
        </Form.Group>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            size="sm"
            variant="falcon-primary"
            disabled={Boolean(busy) || !domain.trim()}
            onClick={() => void onGenerate()}
          >
            {busy === 'generate' ? (
              <>
                <Spinner
                  size="sm"
                  animation="border"
                  className="me-2"
                  style={{ width: 12, height: 12 }}
                />
                در حال ساخت…
              </>
            ) : configured ? (
              'ساخت مجدد کلید'
            ) : (
              'ساخت کلید'
            )}
          </Button>
          {configured ? (
            <Button
              size="sm"
              variant="outline-primary"
              disabled={Boolean(busy)}
              onClick={() => void onTest()}
            >
              {busy === 'test' ? (
                <>
                  <Spinner
                    size="sm"
                    animation="border"
                    className="me-2"
                    style={{ width: 12, height: 12 }}
                  />
                  در حال تست…
                </>
              ) : (
                'تست کلید'
              )}
            </Button>
          ) : null}
        </div>

        {message && (
          <Alert
            variant={message.variant}
            className="border small mb-3"
            dismissible
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {configured && project.indexNowKey && project.indexNowKeyFileUrl ? (
          <div className="d-flex flex-column gap-3">
            <Form.Group>
              <Form.Label>کلید</Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  value={project.indexNowKey}
                  readOnly
                  dir="ltr"
                  className="text-start font-monospace"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() =>
                    void onCopy('key', project.indexNowKey as string)
                  }
                >
                  <IndexerIcon name="copy" className="me-1" />
                  {copied === 'key' ? 'کپی شد' : 'کپی'}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group>
              <Form.Label>آدرس فایل کلید</Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  value={project.indexNowKeyFileUrl}
                  readOnly
                  dir="ltr"
                  className="text-start font-monospace"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() =>
                    void onCopy('url', project.indexNowKeyFileUrl as string)
                  }
                >
                  <IndexerIcon name="copy" className="me-1" />
                  {copied === 'url' ? 'کپی شد' : 'کپی'}
                </Button>
              </InputGroup>
            </Form.Group>
          </div>
        ) : (
          <Alert variant="secondary" className="border small mb-0">
            هنوز کلیدی ساخته نشده. دامنه را وارد کنید و «ساخت کلید» را بزنید.
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}
